import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getUrlDataToMatch, updateUrlData, getMatchesByUserToday, reserveUrlData, releaseUrlData } from '../../../api/url_data';
import { getProduct } from '../../../api/products';
import { useInterval } from '../../../hooks/useInterval';
import { getUrlParam, setUrlParam, deleteUrlParam } from '../../../helpers/helpers';
import cogoToast from 'cogo-toast';

const RESERVED_URL_DATA_POLL_INTERVAL = 1000 * 60; // 1 minute
const DRAINED_QUEUE_RETRY_INTERVAL = 1000 * 30;
const DRAINED_QUEUE_MAX_RETRIES = 4;
const RESOLVED_RESERVATION_REASONS = ['matched', 'dismissed'];
const MIN_FETCH_INTERVAL = 1000; // 1 second - safety guard to prevent calling the API too often
const LIMIT = 70; // Number of items to fetch at a time
const FETCH_THRESHOLD = 10; // Fetch more when there are fewer than this many items left

const UrlDataContext = createContext(null);

const getExcludedDomainsUrlParam = () => {
  const excludedDomains = (getUrlParam('excludedDomains') || '').split(',').filter(Boolean);
  return excludedDomains.length ? excludedDomains : null;
};

const getDefaultFilters = () => ({
  showEscalated: Boolean(getUrlParam('showEscalated')) || null,
  domain: getUrlParam('domain') || null,
  // Rows come back as lowercase hex, while links built from SQL HEX() carry it uppercase
  hashedUrlStem: getUrlParam('hashedUrlStem')?.toLowerCase() || null,
  excludedDomains: getExcludedDomainsUrlParam()
});

export const UrlDataProvider = ({ children }) => {
  // Core functionality states
  const [urlData, setUrlData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hiddenByExclusions, setHiddenByExclusions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentHashedUrlStem, setCurrentHashedUrlStem] = useState(null);
  const [nextOffset, setNextOffset] = useState(0);
  const [reachedEndOfData, setReachedEndOfData] = useState(false);

  const [history, setHistory] = useState([]);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [fetchNonce, setFetchNonce] = useState(0);
  const [drainedRetries, setDrainedRetries] = useState(0);

  // Compute current URL data and index based on hashedUrlStem
  const currentIndex = urlData.findIndex(item => item.hashedUrlStem === currentHashedUrlStem);
  const currentUrlData = currentIndex !== -1 ? urlData[currentIndex] : null;
  const hasNextUrl = currentIndex !== -1 && currentIndex < urlData.length - 1;
  const hasPreviousUrl = currentIndex > 0;

  // Matches track the URL data which the current user has completed recently
  const [matches, setMatches] = useState(new Map());

  // Reservations track URL data which is being worked on by other users to prevent multiple users from working on the same entry
  const [reservations, setReservations] = useState([]);

  // Reservation state
  const [reserving, setReserving] = useState(false);
  const [reservationFailed, setReservationFailed] = useState(false);
  const [reservationFailReason, setReservationFailReason] = useState(null);
  const [reservationExpiresAt, setReservationExpiresAt] = useState(null);
  const [verifyingReservation, setVerifyingReservation] = useState(false);

  // From filter state, not location.search — the param sync uses history.replaceState, which react-router never sees
  const isDirectVisitToCurrentUrl = Boolean(filters.hashedUrlStem) && currentUrlData?.hashedUrlStem === filters.hashedUrlStem;

  // Safety guard to prevent calling the API too often
  const lastFetchTimeRef = useRef(0);

  // Written synchronously when a fetch is armed so effects in the same flush see it — the
  // loading state's closure lags a commit behind
  const fetchPendingRef = useRef(false);

  /**
   * Fetch the URL data from the API when the domain changes or the next offset changes
   */
  useEffect(() => {
    const fetchUrlData = async () => {
      lastFetchTimeRef.current = Date.now();
      setLoading(true);
      setReachedEndOfData(false);

      // A response fetched under old filters must never land in the new list — pagination and
      // drained-queue probes keep the same filters reference, so those still apply.
      const requestFilters = filters;
      const isStale = () => filtersRef.current !== requestFilters;

      try {
        const { data } = await getUrlDataToMatch({ limit: LIMIT, offset: nextOffset, filters });
        if (isStale()) return;

        setUrlData(prev => [...prev, ...data.urlData]);
        setTotalCount(data.totalCount);
        setHiddenByExclusions(data.hiddenByExclusions || 0);

        if (data.urlData.length === 0) {
          setReachedEndOfData(true);
        } else {
          setDrainedRetries(0);
        }

        // Via the ref: the auto-skip can drain the list while this fetch is in flight
        if (data.urlData.length > 0 && !currentHashedUrlStemRef.current) {
          setCurrentHashedUrlStem(data.urlData[0].hashedUrlStem);
        }
      } catch {
        if (!isStale()) {
          // Ends pagination for now instead of letting the fetch-more effect re-fire on the
          // loading flip and walk the offset away from the failed page; the drained-queue
          // probe (or the next filter change) retries from a clean offset
          setReachedEndOfData(true);
          window.ALERT.error('Failed to fetch URL data. Please try again.');
        }
      } finally {
        // A newer fetch owns the loading and pending state once the filters have moved on
        if (!isStale()) {
          fetchPendingRef.current = false;
          setLoading(false);
        }
      }
    };

    fetchPendingRef.current = true;
    setLoading(true);

    // Defer throttled fetches instead of dropping them — a dropped fetch has no retry, so
    // back-to-back filter changes would leave the queue empty under a stale count. Immediate
    // fetches ride the timer too, collapsing same-flush effect re-runs into one request.
    const waitForFetchWindow = Math.max(0, MIN_FETCH_INTERVAL - (Date.now() - lastFetchTimeRef.current));
    const timer = setTimeout(fetchUrlData, waitForFetchWindow);
    return () => clearTimeout(timer);
  }, [nextOffset, filters, fetchNonce]);

  const queueDrained = !loading && urlData.length === 0 && totalCount > 0 && reachedEndOfData;
  // The count can include rows this user can never be served, so give up after a few probes
  // instead of holding the notice forever.
  const allPendingElsewhere = queueDrained && drainedRetries < DRAINED_QUEUE_MAX_RETRIES;

  /**
   * Rows held by others come back once they free up, but nothing refetches on its own once the
   * list drained — re-probe from the top instead of leaving the matcher on a dead end.
   */
  useEffect(() => {
    if (!allPendingElsewhere) return;
    const timer = setTimeout(() => {
      setDrainedRetries(prev => prev + 1);
      setReachedEndOfData(false);
      setNextOffset(0);
      setFetchNonce(prev => prev + 1);
    }, DRAINED_QUEUE_RETRY_INTERVAL);
    return () => clearTimeout(timer);
  }, [allPendingElsewhere]);

  // A discarded reserve response still created the lock server-side — free it so the row
  // doesn't sit dead for other matchers.
  const releaseDiscardedReserve = (hashedUrlStem, data) => {
    if (data?.success) releaseUrlData(hashedUrlStem);
  };

  /**
   * Try to reserve the current URL data when it changes (after matching, navigating to the next or previous URL, etc.)
   *
   * The response includes success/failure status and all reservations, so we can update state accordingly.
   * The agent must wait for a successful reservation before they can start working on a URL.
   */
  useEffect(() => {
    const reserveCurrentUrlData = async () => {
      const hashedUrlStem = currentUrlData?.hashedUrlStem;
      if (!hashedUrlStem) return;

      setReserving(true);
      setReservationFailed(false);
      setReservationFailReason(null);

      try {
        const { data } = await reserveUrlData(hashedUrlStem);

        // Ignore stale response if user navigated away during the request
        if (currentHashedUrlStemRef.current !== hashedUrlStem) {
          releaseDiscardedReserve(hashedUrlStem, data);
          return;
        }

        const reservationsList = data.reservations || data;
        setReservations(reservationsList);

        if (data.ttl) {
          setReservationExpiresAt(Date.now() + data.ttl * 1000);
        }

        if (data.success === false) {
          // A stale list entry — skip ahead instead of parking the agent on a banner
          const isDirectVisit = Boolean(filters.hashedUrlStem) && filters.hashedUrlStem === hashedUrlStem;
          if (!isDirectVisit && ['reserved', ...RESOLVED_RESERVATION_REASONS].includes(data.reason)) {
            // Reserved rows are still pending server-side, so they keep their slot in the total
            removeUrlData(hashedUrlStem, { release: false, decrementTotal: data.reason !== 'reserved' });
            return;
          }
          setReservationFailed(true);
          setReservationFailReason(data.reason);
          setReservationExpiresAt(null);
        }
      } catch {
        // Ignore stale error if user navigated away during the request
        if (currentHashedUrlStemRef.current !== hashedUrlStem) return;

        setReservationFailed(true);
        setReservationFailReason('error');
        setReservationExpiresAt(null);
        cogoToast.error('Failed to reserve URL. Please try again.');
      } finally {
        // Only clear reserving state if still on the same URL
        if (currentHashedUrlStemRef.current === hashedUrlStem) {
          setReserving(false);
        }
      }
    };

    reserveCurrentUrlData();
  }, [currentUrlData?.hashedUrlStem]);

  /**
   * Fetch more URL data when the current index is close to the end of the list
   */
  useEffect(() => {
    // A stem lookup is one row and the server ignores offset for it, so paging would refetch the same row
    if (filters.hashedUrlStem) return;
    if (currentIndex > urlData.length - FETCH_THRESHOLD && !loading && !fetchPendingRef.current && totalCount > urlData.length && !reachedEndOfData) {
      setNextOffset(prev => prev + LIMIT);
    }
  }, [urlData, totalCount, currentIndex, loading, filters.hashedUrlStem]);

  useEffect(() => {
    const fetchHistory = async () => {
      const { data } = await getMatchesByUserToday();
      setHistory(data);
    };

    fetchHistory();
  }, []);

  // Use refs to always have access to current values without closure issues and so the
  // poller and visibility listener can access them without considering them updated dependencies
  const currentHashedUrlStemRef = useRef(currentUrlData?.hashedUrlStem);
  const reservationExpiresAtRef = useRef(reservationExpiresAt);
  const urlDataRef = useRef(urlData);
  const isDirectVisitRef = useRef(isDirectVisitToCurrentUrl);
  const filtersRef = useRef(filters);

  // Ensure the refs are up-to-date all the time
  currentHashedUrlStemRef.current = currentUrlData?.hashedUrlStem;
  reservationExpiresAtRef.current = reservationExpiresAt;
  urlDataRef.current = urlData;
  isDirectVisitRef.current = isDirectVisitToCurrentUrl;
  filtersRef.current = filters;

  /**
   * Get reserved URLs and remove them from `urlData`, unless its the entry the user is currently seeing (so we don't create
   * blank pages, or flickering while briefly seeing the current url data)
   */
  const reservationsPoller = useCallback(async () => {
    try {
      const hashedUrlStem = currentHashedUrlStemRef.current;
      if (!hashedUrlStem) return;

      const { data } = await reserveUrlData(hashedUrlStem);

      // Ignore stale response if user navigated away during the request
      if (currentHashedUrlStemRef.current !== hashedUrlStem) {
        releaseDiscardedReserve(hashedUrlStem, data);
        return;
      }

      const reservationsList = data.reservations || data;
      setReservations(reservationsList);

      if (data.ttl) {
        setReservationExpiresAt(Date.now() + data.ttl * 1000);
      }

      if (data.success === false) {
        if (RESOLVED_RESERVATION_REASONS.includes(data.reason) && !isDirectVisitRef.current) {
          // Resolved underneath us — let the agent finish; their save lands as a late correction
          setReservationFailed(false);
          setReservationFailReason(null);
        } else {
          // The reservation was genuinely lost to another agent: block before edits collide
          setReservationFailed(true);
          setReservationFailReason(data.reason);
          setReservationExpiresAt(null);
        }
      } else if (data.success === true) {
        setReservationFailed(false);
        setReservationFailReason(null);
      }

      if (reservationsList.length > 0) {
        const reservedUrlData = new Set(reservationsList.map(reservation => reservation.hashedUrlStem));

        setUrlData(prev => {
          const hasReservedItemsInUrlData = prev.some(urlDataItem => reservedUrlData.has(urlDataItem.hashedUrlStem));

          if (!hasReservedItemsInUrlData) {
            return prev;
          }

          return prev.filter(urlDataItem => {
            // If this item is not reserved, keep it
            if (!reservedUrlData.has(urlDataItem.hashedUrlStem)) {
              return true;
            }

            // If this item is reserved but it's the current URL data, keep it
            if (urlDataItem.hashedUrlStem === currentHashedUrlStemRef.current) {
              return true;
            }

            // Otherwise, this item is reserved by someone else, so remove it
            return false;
          });
        });
      }
    } catch {
      cogoToast.error('Error fetching reserved URL data. If the error persists, please refresh the page.');
    }
  }, []);

  // Run the poller. Stop if there's no more URL data to fetch.
  useInterval(reservationsPoller, totalCount > 0 ? RESERVED_URL_DATA_POLL_INTERVAL : null);

  /**
   * Verify reservation when the page becomes visible again (e.g., after computer wakes from sleep).
   * This ensures agents see an error immediately if their reservation expired while away.
   * Only reloads if TTL is less than 1 minute left to avoid blocking the interface on each focus change.
   */
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const hashedUrlStem = currentHashedUrlStemRef.current;
      if (!hashedUrlStem) return;

      // Only reload if TTL is less than 1 minute left
      const oneMinuteInMs = 60 * 1000;
      const timeRemaining = reservationExpiresAtRef.current ? reservationExpiresAtRef.current - Date.now() : null;

      // If TTL is more than 1 minute left, do nothing
      if (timeRemaining !== null && timeRemaining > oneMinuteInMs) {
        return;
      }

      // Check if local TTL has expired - show immediate warning before network check
      const ttlExpired = reservationExpiresAtRef.current && Date.now() > reservationExpiresAtRef.current;

      setVerifyingReservation(true);

      if (ttlExpired) {
        // Show warning state immediately - don't wait for network
        setReservationFailed(true);
        setReservationFailReason('expired');
      }

      try {
        const { data } = await reserveUrlData(hashedUrlStem);

        // Ignore stale response if user navigated away during the request
        if (currentHashedUrlStemRef.current !== hashedUrlStem) {
          releaseDiscardedReserve(hashedUrlStem, data);
          return;
        }

        const reservationsList = data.reservations || data;
        setReservations(reservationsList);

        if (data.ttl) {
          setReservationExpiresAt(Date.now() + data.ttl * 1000);
        } else {
          setReservationExpiresAt(null);
        }

        if (data.success || (RESOLVED_RESERVATION_REASONS.includes(data.reason) && !isDirectVisitRef.current)) {
          // Reservation intact — or resolved while away, in which case the agent can finish
          setReservationFailed(false);
          setReservationFailReason(null);
        } else {
          setReservationFailed(true);
          setReservationFailReason(data.reason);
        }
      } catch {
        // Ignore stale error if user navigated away during the request
        if (currentHashedUrlStemRef.current !== hashedUrlStem) return;

        setReservationFailed(true);
        setReservationFailReason('error');
      } finally {
        // Only clear verifying state if still on the same URL
        if (currentHashedUrlStemRef.current === hashedUrlStem) {
          setVerifyingReservation(false);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const goToNextUrl = () => {
    if (hasNextUrl && currentIndex !== -1) {
      if (currentUrlData) releaseUrlData(currentUrlData.hashedUrlStem);
      const nextItem = urlData[currentIndex + 1];
      if (nextItem) {
        setCurrentHashedUrlStem(nextItem.hashedUrlStem);
      }
    }
  };

  const goToPreviousUrl = () => {
    if (hasPreviousUrl && currentIndex !== -1) {
      if (currentUrlData) releaseUrlData(currentUrlData.hashedUrlStem);
      const prevItem = urlData[currentIndex - 1];
      if (prevItem) {
        setCurrentHashedUrlStem(prevItem.hashedUrlStem);
      }
    }
  };

  /** Remove an entry, advancing to a neighbor. Reads refs — async callers hold stale closures. */
  const removeUrlData = (hashedUrlStem, { release = true, decrementTotal = true } = {}) => {
    if (release) releaseUrlData(hashedUrlStem);

    const list = urlDataRef.current;
    const removedIndex = list.findIndex(item => item.hashedUrlStem === hashedUrlStem);

    if (hashedUrlStem === currentHashedUrlStemRef.current && removedIndex !== -1) {
      const remaining = list.filter(item => item.hashedUrlStem !== hashedUrlStem);
      const nextItem = remaining[removedIndex] || remaining[removedIndex - 1] || null;
      setCurrentHashedUrlStem(nextItem ? nextItem.hashedUrlStem : null);
    }

    setUrlData(prev => prev.filter(item => item.hashedUrlStem !== hashedUrlStem));
    if (decrementTotal) setTotalCount(prev => prev - 1);
  };

  const changeFilters = newFilters => {
    // Leaving via a filter change abandons the current pin — free its lock for other matchers
    if (currentHashedUrlStemRef.current) releaseUrlData(currentHashedUrlStemRef.current);
    setUrlData([]);
    // Marking as loading right away keeps the fetch-more effect from bumping the offset off the
    // cleared list, which would point the refetch at the wrong page
    setLoading(true);
    setReachedEndOfData(false);
    setDrainedRetries(0);
    setFilters(newFilters);
    setCurrentHashedUrlStem(null); // Reset to null, will be set when data loads
    setNextOffset(0);
  };

  const resetFilters = () => {
    const clearedFilters = Object.keys(filters).reduce((acc, key) => ({ ...acc, [key]: null }), {});
    changeFilters(clearedFilters);
  };

  const excludedDomains = filters.excludedDomains || [];

  const excludeDomain = domain => {
    if (excludedDomains.includes(domain)) return;
    changeFilters({
      ...filters,
      // The include filter and an exclusion for the same domain would return zero rows
      domain: filters.domain === domain ? null : filters.domain,
      excludedDomains: [...excludedDomains, domain]
    });
  };

  const removeExcludedDomain = domain => {
    const remaining = excludedDomains.filter(excludedDomain => excludedDomain !== domain);
    changeFilters({ ...filters, excludedDomains: remaining.length ? remaining : null });
  };

  const clearExcludedDomains = () => {
    changeFilters({ ...filters, excludedDomains: null });
  };

  /**
   * Sync filters with URL params
   */
  useEffect(() => {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        setUrlParam(key, value);
      } else {
        deleteUrlParam(key);
      }
    });
  }, [filters]);

  /**
   * Match the URL data to the specified product. This is called by the queueMatch function. Several could be running
   * in parallel.
   */
  const matchUrlDataToProduct = async ({ hashedUrlStem, productId, metadata = {}, overrideSecondOpinion = false }) => {
    try {
      const humanCatalogAction = metadata.humanCatalogAction || (metadata.userCreatedProduct ? 'create_product' : 'attach_existing_product');
      const { data } = await updateUrlData(hashedUrlStem, {
        Product_id: productId,
        metadata: { ...metadata, humanCatalogAction },
        ...(overrideSecondOpinion ? { overrideSecondOpinion: true } : {})
      });

      // Fetch the product to get the brand and title to add to the history without having to refetch the entire history
      const product = await getProduct(productId, {});
      setHistory(prev => [
        {
          id: data.id,
          hashedUrlStem,
          brandName: product.brand,
          productTitle: product.title,
          productId: product.id,
          matchedAt: data.matchedAt
        },
        ...prev
      ]);

      if (data.updatedPinCount) {
        cogoToast.success(`Successfully matched product (${data.updatedPinCount} pins updated)`);
      } else {
        cogoToast.success('Successfully matched product. No pins updated.');
      }
      return data;
    } catch (error) {
      cogoToast.error(typeof error === 'string' ? error : 'There was an issue matching the product');
    }
  };

  /**
   * Queue a match for the URL data so we can visualize pending updates and continue on to the next item
   * while the match is in progress in the background
   */
  const queueMatch = ({ hashedUrlStem, productId, metadata }) => {
    // Prevent duplicate submissions for the same URL
    if (matches.has(hashedUrlStem)) {
      return;
    }

    // Add to the queue so we can visualize pending updates
    setMatches(prev => {
      const newMatches = new Map(prev);
      newMatches.set(hashedUrlStem, productId);
      return newMatches;
    });

    // Remove from the UI but keep the reservation
    removeUrlData(hashedUrlStem, { release: false });

    // Start the request to match
    matchUrlDataToProduct({
      hashedUrlStem,
      productId,
      metadata,
      overrideSecondOpinion: isDirectVisitToCurrentUrl
    }).then(() => {
      // Remove from the queue when done
      setMatches(prev => {
        const newMatches = new Map(prev);
        newMatches.delete(hashedUrlStem);
        return newMatches;
      });
    });
  };

  return (
    <UrlDataContext.Provider
      value={{
        urlData,
        totalCount,
        loading,
        allPendingElsewhere,
        currentIndex,
        currentUrlData,
        reservations,
        reserving,
        verifyingReservation,
        reservationFailed,
        reservationFailReason,
        isCurrentUrlReserved: reservationFailed && !isDirectVisitToCurrentUrl,
        isCurrentUrlBlocked: (reserving || verifyingReservation || reservationFailed) && !isDirectVisitToCurrentUrl,
        isDirectVisitToCurrentUrl,
        goToNextUrl,
        goToPreviousUrl,
        removeUrlData,
        hasNextUrl,
        hasPreviousUrl,
        filters,
        changeFilters,
        resetFilters,
        excludedDomains,
        hiddenByExclusions,
        excludeDomain,
        removeExcludedDomain,
        clearExcludedDomains,
        queueMatch,
        matches,
        history
      }}
    >
      {children}
    </UrlDataContext.Provider>
  );
};

export const useUrlDataContext = () => {
  const context = useContext(UrlDataContext);
  if (!context) {
    throw new Error('useUrlDataContext must be used within a UrlDataProvider');
  }
  return context;
};
